import React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, GraduationCap, Briefcase, Award } from 'lucide-react';
import { Badge } from '../components/ui/badge';

export const JuryBioPage = () => {
  const [searchParams] = useSearchParams();
  const index = parseInt(searchParams.get('index') || '0');

  const juryMembers = [
    {
      name: 'Sarah Mitchell',
      title: 'Chief Creative Officer',
      organization: 'Global Exhibits Inc.',
      experience: '30+ years in exhibition design',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=800&fit=crop',
      education: 'Master of Fine Arts in Industrial Design, Rhode Island School of Design',
      specialties: ['Brand Experience', 'Spatial Design', 'Interactive Installations'],
      biography: 'Sarah Mitchell has been at the forefront of exhibition design for over three decades. Her innovative approaches have revolutionized how brands present themselves at trade shows worldwide. She has led the creative direction for over 500 major exhibitions across 40 countries.',
      awards: [
        'Best Exhibition Designer 2020',
        'Lifetime Achievement Award 2019',
        'Innovation in Design Award 2018'
      ]
    },
    {
      name: 'Dr. Marcus Chen',
      title: 'Professor of Design',
      organization: 'Royal College of Art',
      experience: '25+ years in design education',
      image: 'https://images.unsplash.com/photo-1556157382-97eda2d62296?w=600&h=800&fit=crop',
      education: 'PhD in Design Theory, Royal College of Art, London',
      specialties: ['Sustainable Design', 'Design Theory', 'Material Science'],
      biography: 'Dr. Chen is a leading academic voice in sustainable design practices. His research has influenced exhibition design standards globally. He has published over 50 papers and authored "The Future of Exhibition Design", considered essential reading in the field.',
      awards: [
        'Academic Excellence Award 2021',
        'Sustainability Leadership Award 2020',
        'Best Research Publication 2019'
      ]
    },
    {
      name: 'Maria Rodriguez',
      title: 'Chief Experience Officer',
      organization: 'Innovative Spaces Inc',
      experience: '20+ years in experiential design',
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=600&h=800&fit=crop',
      education: 'Master of Architecture, MIT',
      specialties: ['AR/VR Integration', 'Digital Experiences', 'Immersive Technology'],
      biography: 'Maria Rodriguez is a pioneer in technology-driven exhibition solutions. Her work in AR/VR integration has set new standards for interactive exhibits. She has designed experiences for Fortune 500 companies including Apple, Google, and Tesla.',
      awards: [
        'Tech Innovation Award 2022',
        'Digital Experience Excellence 2021',
        'Best Interactive Installation 2020'
      ]
    },
    {
      name: 'James Chen',
      title: 'Head of Design',
      organization: 'European Trade Shows',
      experience: '28+ years in exhibition architecture',
      image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=600&h=800&fit=crop',
      education: 'Bachelor of Architecture, ETH Zurich',
      specialties: ['Large-Scale Architecture', 'Structural Engineering', 'Project Management'],
      biography: 'James Chen specializes in large-scale exhibition architecture with projects across 40+ countries. His expertise in complex structural systems has enabled some of the most ambitious exhibition stands ever built, including award-winning installations at major trade shows worldwide.',
      awards: [
        'Best Structural Design 2022',
        'International Architecture Award 2021',
        'Engineering Excellence 2019'
      ]
    },
    {
      name: 'Lisa Anderson',
      title: 'Brand Strategist',
      organization: 'Momentum Agency',
      experience: '18+ years in brand experience',
      image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600&h=800&fit=crop',
      education: 'MBA in Marketing, Harvard Business School',
      specialties: ['Brand Strategy', 'Consumer Psychology', 'Storytelling'],
      biography: 'Lisa Anderson is renowned for creating brand-driven experiences that resonate with audiences. Her strategic approach combines consumer psychology with spatial design to create memorable brand activations. She has worked with brands like Nike, Coca-Cola, and BMW.',
      awards: [
        'Brand Experience of the Year 2023',
        'Marketing Excellence Award 2022',
        'Best Brand Activation 2021'
      ]
    },
    {
      name: 'Michael Roberts',
      title: 'Senior Designer',
      organization: 'Studio Norte',
      experience: '22+ years in interactive design',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=800&fit=crop',
      education: 'Master of Interactive Design, Parsons School of Design',
      specialties: ['Interactive Installations', 'Sensor Technology', 'User Experience'],
      biography: 'Michael Roberts focuses on interactive installations that engage visitors through cutting-edge technology. His work has been featured in major museums and exhibitions worldwide. He specializes in creating intuitive, engaging experiences that bridge physical and digital realms.',
      awards: [
        'Interactive Design Award 2023',
        'Best User Experience 2022',
        'Innovation in Technology 2020'
      ]
    },
    {
      name: 'Emily Watson',
      title: 'Vice President',
      organization: 'Design Collective',
      experience: '24+ years in visitor experience',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&h=800&fit=crop',
      education: 'Master of Fine Arts, Yale University',
      specialties: ['Visitor Journey Mapping', 'Experience Design', 'Engagement Strategy'],
      biography: 'Emily Watson has dedicated her career to creating exceptional visitor experiences. Her methodology for journey mapping has been adopted by leading exhibition companies worldwide. She has designed experiences that have welcomed over 10 million visitors globally.',
      awards: [
        'Visitor Experience Excellence 2023',
        'Best Journey Design 2022',
        'Customer Engagement Award 2021'
      ]
    },
    {
      name: 'David Kim',
      title: 'Innovative Director',
      organization: 'Innovate Designs',
      experience: '19+ years in design innovation',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&h=800&fit=crop',
      education: 'Master of Design Innovation, Stanford University',
      specialties: ['Emerging Technology', 'AI Integration', 'Future Trends'],
      biography: 'David Kim focuses on emerging technologies and their application in exhibition design. His forward-thinking approach has introduced AI-powered personalization and predictive analytics to the exhibition industry. He regularly speaks at international design conferences.',
      awards: [
        'Innovation Leader Award 2023',
        'Technology Pioneer 2022',
        'Future of Design Award 2021'
      ]
    },
    {
      name: 'Sophie Laurent',
      title: 'Brand Designer',
      organization: 'Nordic Designs',
      experience: '17+ years in minimalist design',
      image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=600&h=800&fit=crop',
      education: 'Master of Architecture, Aalto University',
      specialties: ['Minimalist Architecture', 'Scandinavian Design', 'Material Innovation'],
      biography: 'Sophie Laurent is renowned for her minimalist approach to exhibition design. Her Scandinavian-influenced aesthetic has influenced design trends globally. She specializes in creating elegant, functional spaces that emphasize quality over quantity.',
      awards: [
        'Minimalist Design Award 2023',
        'Scandinavian Design Excellence 2022',
        'Best Material Use 2021'
      ]
    },
    {
      name: 'Robert Johnson',
      title: 'Experience Lead',
      organization: 'Global Events',
      experience: '26+ years in experiential marketing',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600&h=800&fit=crop',
      education: 'Bachelor of Marketing, University of London',
      specialties: ['Experiential Marketing', 'Event Strategy', 'Audience Engagement'],
      biography: 'Robert Johnson brings deep expertise in experiential marketing to exhibition design. His strategic approach ensures that every stand delivers measurable business results. He has managed exhibitions for over 200 global brands across various industries.',
      awards: [
        'Experiential Marketing Leader 2023',
        'Best Event Strategy 2022',
        'Marketing Impact Award 2021'
      ]
    },
    {
      name: 'Yuki Tanaka',
      title: 'Chief Architect',
      organization: 'Tokyo Design Week',
      experience: '21+ years in modern architecture',
      image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=600&h=800&fit=crop',
      education: 'Master of Architecture, Tokyo University of the Arts',
      specialties: ['Modern Exhibition Spaces', 'Japanese Aesthetics', 'Spatial Planning'],
      biography: 'Yuki Tanaka combines traditional Japanese aesthetics with modern exhibition design principles. His work is characterized by attention to detail and respect for spatial harmony. He has designed pavilions for major international exhibitions including Expo Milan and Dubai.',
      awards: [
        'Architectural Excellence Award 2023',
        'Best Pavilion Design 2022',
        'Japanese Design Award 2021'
      ]
    },
    {
      name: 'Isabella Rossi',
      title: 'Sustainability Expert',
      organization: 'Green Events Group',
      experience: '16+ years in sustainable design',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=600&h=800&fit=crop',
      education: 'Master of Environmental Design, University of Cambridge',
      specialties: ['Circular Economy Design', 'Sustainable Materials', 'Zero Waste'],
      biography: 'Isabella Rossi is an advocate for circular economy principles in exhibition design. Her consulting has helped hundreds of companies reduce their environmental impact while maintaining design excellence. She has developed industry-leading sustainability frameworks.',
      awards: [
        'Sustainability Champion 2023',
        'Green Design Award 2022',
        'Environmental Leadership 2021'
      ]
    },
    {
      name: 'Thomas Wagner',
      title: 'Senior Architect',
      organization: 'Berlin Design Studio',
      experience: '23+ years in structural innovation',
      image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=600&h=800&fit=crop',
      education: 'Diploma in Architecture, Technical University of Berlin',
      specialties: ['Structural Innovation', 'Engineering Solutions', 'Technical Design'],
      biography: 'Thomas Wagner specializes in innovative structural solutions for exhibition stands. His engineering expertise enables ambitious designs that push the boundaries of what\'s possible. He has received numerous patents for his structural innovations in temporary architecture.',
      awards: [
        'Structural Innovation Award 2023',
        'Engineering Excellence 2022',
        'Technical Design Award 2021'
      ]
    },
    {
      name: 'Amanda Peters',
      title: 'Design Director',
      organization: 'West Coast Exhibits',
      experience: '20+ years in brand storytelling',
      image: 'https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=600&h=800&fit=crop',
      education: 'Bachelor of Fine Arts, California College of the Arts',
      specialties: ['Brand Storytelling', 'Visual Communication', 'Creative Direction'],
      biography: 'Amanda Peters excels at translating brand stories into immersive spatial experiences. Her creative direction has won numerous industry awards. She believes that great exhibition design tells a story that resonates emotionally with visitors.',
      awards: [
        'Creative Director of the Year 2023',
        'Best Brand Storytelling 2022',
        'Visual Excellence Award 2021'
      ]
    },
    {
      name: 'Carlos Martinez',
      title: 'Executive Producer',
      organization: 'Latin Events Network',
      experience: '27+ years in international exhibitions',
      image: 'https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?w=600&h=800&fit=crop',
      education: 'Bachelor of Business Administration, Universidad de Buenos Aires',
      specialties: ['International Exhibitions', 'Project Management', 'Logistics'],
      biography: 'Carlos Martinez is a veteran producer with extensive experience in managing high-profile international exhibitions. His operational expertise ensures flawless execution of complex projects. He has produced exhibitions in over 60 countries across six continents.',
      awards: [
        'Production Excellence Award 2023',
        'Best Project Management 2022',
        'International Achievement 2021'
      ]
    },
    {
      name: 'Jennifer White',
      title: 'Design Consultant',
      organization: 'Design Forward',
      experience: '15+ years in conceptual design',
      image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&h=800&fit=crop',
      education: 'Master of Design, Central Saint Martins',
      specialties: ['Conceptual Design', 'Creative Strategy', 'Design Thinking'],
      biography: 'Jennifer White brings fresh perspectives to exhibition design through her consultancy work. Her conceptual approach helps brands rethink how they present themselves. She has advised Fortune 500 companies on exhibition strategy and design innovation.',
      awards: [
        'Consultant of the Year 2023',
        'Best Design Strategy 2022',
        'Creative Excellence 2021'
      ]
    },
    {
      name: 'Daniel Brooks',
      title: 'Art Director',
      organization: 'Creative Minds Inc',
      experience: '19+ years in visual storytelling',
      image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=600&h=800&fit=crop',
      education: 'Bachelor of Visual Arts, School of Visual Arts NYC',
      specialties: ['Visual Storytelling', 'Art Direction', 'Graphic Integration'],
      biography: 'Daniel Brooks specializes in visual storytelling that creates emotional connections with visitors. His art direction seamlessly integrates graphics, lighting, and spatial design. He has directed creative for exhibitions at major venues including the Louvre and MoMA.',
      awards: [
        'Art Direction Award 2023',
        'Visual Excellence 2022',
        'Best Graphic Integration 2021'
      ]
    },
    {
      name: 'Rachel Green',
      title: 'Experience Designer',
      organization: 'Interaction Studio',
      experience: '14+ years in user-centric design',
      image: 'https://images.unsplash.com/photo-1557296387-5358ad7997bb?w=600&h=800&fit=crop',
      education: 'Master of Interaction Design, Carnegie Mellon University',
      specialties: ['User-Centric Design', 'Behavioral Analysis', 'Design Research'],
      biography: 'Rachel Green focuses on user-centric design that prioritizes visitor needs and behaviors. Her research-driven approach ensures that exhibitions deliver optimal experiences. She has conducted usability studies for over 100 major exhibitions worldwide.',
      awards: [
        'UX Design Excellence 2023',
        'Best Research Application 2022',
        'User-Centered Design Award 2021'
      ]
    },
    {
      name: 'Alexander Lee',
      title: 'Creative Lead',
      organization: 'Future Exhibits',
      experience: '18+ years in immersive environments',
      image: 'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=600&h=800&fit=crop',
      education: 'Master of Environmental Design, University of California',
      specialties: ['Immersive Environments', 'Sensory Design', 'Multi-Sensory Experiences'],
      biography: 'Alexander Lee creates immersive environments that engage all five senses. His multi-sensory approach to exhibition design has redefined visitor engagement. He pioneered the use of scent, sound, and touch in exhibition environments.',
      awards: [
        'Immersive Experience Award 2023',
        'Sensory Design Excellence 2022',
        'Best Multi-Sensory Installation 2021'
      ]
    },
    {
      name: 'Victoria Chen',
      title: 'Branding Expert',
      organization: 'Brand Vision Co',
      experience: '16+ years in brand activations',
      image: 'https://images.unsplash.com/photo-1598550874175-4d0ef436c909?w=600&h=800&fit=crop',
      education: 'Master of Brand Management, London Business School',
      specialties: ['Brand Activations', 'Identity Design', 'Market Positioning'],
      biography: 'Victoria Chen specializes in brand activations that create lasting impressions. Her strategic expertise helps brands maximize their exhibition ROI. She has developed activation strategies for over 300 brands across diverse industries.',
      awards: [
        'Brand Activation Excellence 2023',
        'Best Brand Integration 2022',
        'Marketing Strategy Award 2021'
      ]
    }
  ];

  const member = juryMembers[index] || juryMembers[0];

  return (
    <div className="min-h-screen bg-white">
      {/* Back Button */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/jury"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          data-testid="back-to-jury"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Jury Panel
        </Link>
      </div>

      {/* Hero Section */}
      <section className="pb-16">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Photo */}
            <div className="w-full max-w-md mx-auto lg:mx-0">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-gray-200">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Info */}
            <div className="space-y-6">
              <div>
                <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-3">
                  {member.name}
                </h1>
                <p className="text-xl text-gray-700 mb-2">
                  {member.title}
                </p>
                <p className="text-lg text-amber-600 font-semibold mb-4">
                  {member.organization}
                </p>
                <p className="text-gray-600 italic">
                  {member.experience}
                </p>
              </div>

              {/* Education */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <GraduationCap className="h-5 w-5 text-gray-700" />
                  <h3 className="text-lg font-bold text-gray-900">Education</h3>
                </div>
                <p className="text-gray-700">
                  {member.education}
                </p>
              </div>

              {/* Specialties */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase className="h-5 w-5 text-gray-700" />
                  <h3 className="text-lg font-bold text-gray-900">Specialties</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {member.specialties.map((specialty, idx) => (
                    <Badge
                      key={idx}
                      className="bg-black text-white px-4 py-1.5 text-sm font-medium"
                    >
                      {specialty}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Biography Section */}
      <section className="py-16 bg-white">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Biography</h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            {member.biography}
          </p>
        </div>
      </section>

      {/* Awards Section */}
      <section className="py-16 bg-amber-50">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <Award className="h-6 w-6 text-amber-600" />
            <h2 className="text-3xl font-bold text-gray-900">Awards & Recognition</h2>
          </div>
          <ul className="space-y-3">
            {member.awards.map((award, idx) => (
              <li key={idx} className="flex items-start gap-3 text-lg text-gray-700">
                <span className="text-amber-600 mt-1">●</span>
                <span>{award}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};
